import React from 'react';
import { connect } from 'react-redux';
import cx from 'classnames';

import appLogger from 'app/shared/loggers/appLogger';

import PropTypes from 'prop-types';

import {
    PageBreadcrumb,
    Text,
    TextButton,
} from '@zillow/constellation';

import {
    Col,
    Container,
    Row,
} from '@zillow/zrs-comet';

// rmw context
import { RMWContext } from 'app/shared/providers/RMWContext';

import { Link } from 'react-router-dom';

// Actions
import LeadActions from 'app/shared/actions/LeadActions';

// containers
import AuthCheckWrapper from 'app/shared/containers/AuthCheckWrapper';

import analyticsUtils from 'app/client/utils/analyticsUtils';
import HeadWrapper from 'app/shared/components/HeadWrapper';
import LeadResume from 'app/shared/components/lead/LeadResume';
import LeadRequest from 'app/shared/components/lead/LeadRequest';
import SpinnerContainer from 'app/shared/components/SpinnerContainer';

import userUtils from 'app/shared/utils/userUtils';

// constants
import Constants from 'app/shared/constants';
import AnalyticsConstants from 'app/shared/constants/AnalyticsConstants';

// Selectors
import {
    AppSelectors, LeadSelectors, RoutingSelectors, UserSelectors,
} from 'app/shared/selectors';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';

import './style.scss';

const propTypes = {
    lead: PropTypes.object.isRequired,
};

class LeadPage extends React.Component {
    static contextType = RMWContext;

    static contextTypes = {
        paths: PropTypes.object,
    };

    constructor(props) {
        super(props);

        this.state = {
            isLoading: true,
            isFound: false,
        };

        this.fetchLead = this.fetchLead.bind(this);
        this.renderLoadComplete = this.renderLoadComplete.bind(this);
        this.renderBreadcrumb = this.renderBreadcrumb.bind(this);
        this.handleApplyButtonClick = this.handleApplyButtonClick.bind(this);
    }

    componentDidMount() {
        const { match } = this.props;
        const { leadId } = match.params;

        this.fetchLead(leadId);
    }

    fetchLead(leadId) {
        if (!leadId) {
            return console.warn('ERROR: leadId is required');
        }

        const { dispatch, roles } = this.props;

        this.setState({ isLoading: true });

        dispatch(LeadActions.getLead({ leadId, includeListingDetails: true }))
            .then(() => {
                this.setState({
                    isLoading: false,
                    isFound: true,
                });
            })

            // update read status for inquiry
            .then(() => {
                const isPostingPathAdmin = userUtils.isPostingPathAdmin(roles);

                if (!isPostingPathAdmin) {
                    dispatch(LeadActions.updateLeadStatus({ leadId, read: true }));
                }
            })
            .catch((err) => {
                appLogger.logError({
                    message: err,
                    context: 'LeadPage.fetchLead',
                });

                this.setState({
                    isLoading: false,
                    isFound: false,
                });
            });

        return false;
    }

    handleApplyButtonClick() {
        const {
            dispatch,
        } = this.props;

        // GA tracking
        dispatch(analyticsUtils.track.GAEventV2(AnalyticsConstants.GA_EVENTS.APPLICATIONS_INVITE_TO_APPLY));
    }

    isLeadEligibleForApply() {
        const { brand, lead } = this.props;

        if (isEmpty(lead.listing.listingDetails)) {
            return false;
        }

        const isZillow = brand === Constants.UNFORMATTED_BRAND_NAMES.zillow;

        return isZillow &&
            !lead.inquiry.isPhoneLead &&
            !lead.listing.listingDetails.isMultifamily;
    }

    renderBreadcrumb() {
        const { paths } = this.context;
        const { street, listingId } = this.props.lead.listing;

        return (
            <PageBreadcrumb
                backButton={(
                    <TextButton
                        as={Link}
                        to={`${paths.base}/properties/${listingId}/${paths.propertyDetailsContacts}`}
                    >
                        {`Contacts for ${street}`}
                    </TextButton>
                )}
            />
        );
    }

    isEmptyLeadResume(leadData) {
        if (!leadData) {
            return true;
        }

        const {
            credit,
            employer,
            isSmoker,
            income,
            jobTitle,
            moveInTimeframe,
            pets,
            numOccupants,
        } = leadData.attributes;

        const isEmptyResponse =
            isEmpty(leadData.attributes) ||
                (isNil(credit) &&
                isNil(employer) &&
                isNil(isSmoker) &&
                isNil(income) &&
                isNil(jobTitle) &&
                isNil(moveInTimeframe) &&
                (pets && pets.length === 0) &&
                isNil(numOccupants));

        return isEmptyResponse;
    }

    renderInviteToApply() {
        const { leadId } = this.props.match.params;
        const { paths } = this.context;
        const { listingId } = this.props.lead.listing;
        const basePropertyUrl = paths.propertiesPage;
        const applicationsPage = paths.propertyDetailsApplications;
        const applicationsInviteUrl = `${basePropertyUrl}/${listingId}/${applicationsPage}?leadId=${leadId}`;

        if (!this.isLeadEligibleForApply()) {
            return null;
        }

        return (
            <React.Fragment>
                <Row className="LeadPage-Invitation">
                    <Col md={5}>
                        <Link
                            data-test="apply-button"
                            className="LeadPage-Button stacked-md Button Button-primary Button-full"
                            to={applicationsInviteUrl}
                            onClick={this.handleApplyButtonClick}
                        >
                                Invite to apply
                        </Link>
                    </Col>
                </Row>
            </React.Fragment>
        );
    }

    renderLoadComplete(leadData, listingData) {
        const { isScreenMobile } = this.props;

        if (this.state.isFound) {
            return (
                <Container className="LeadPage-Container" fluidMobile={true}>
                    {this.renderBreadcrumb()}

                    <Row className="LeadPage-Row" noPadding={true}>
                        <Col>
                            <LeadRequest
                                isScreenMobile={isScreenMobile}
                                leadData={leadData}
                                listingData={listingData}
                            >
                                {this.renderInviteToApply()}
                            </LeadRequest>
                        </Col>
                    </Row>

                    {!this.isEmptyLeadResume(leadData) && (
                        <Row
                            className={cx([
                                'LeadPage-RenterResume',
                                'LeadPage-Row',
                            ])}
                            noPadding={true}
                        >
                            <Col>
                                <LeadResume leadData={leadData} />
                            </Col>
                        </Row>
                    )}
                </Container>
            );
        }
        return (
            <Container>
                <Row>
                    <Col>
                        <Text>Inquiry not found</Text>
                    </Col>
                </Row>
            </Container>
        );
    }

    render() {
        const { lead } = this.props;
        const titleContent = lead.listing ? `Inquiry for ${lead.listing.name}` : 'Inquiry';

        return (
            <AuthCheckWrapper requireStrongAuthentication={false}>
                <div className="LeadPage">
                    <HeadWrapper title={titleContent} />
                    {this.state.isLoading ?
                        <SpinnerContainer /> :
                        this.renderLoadComplete(lead.inquiry, lead.listing)}
                </div>
            </AuthCheckWrapper>
        );
    }
}

const mapStateToProps = state => ({
    isScreenMobile: AppSelectors.makeIsScreenMobile(state),
    lead: LeadSelectors.makeLead(state),
    user: UserSelectors.makeUser(state),
    location: RoutingSelectors.makeLocation(state),
    roles: UserSelectors.makeRoles(state),
    brand: AppSelectors.makeBrand(state),
});

LeadPage.propTypes = propTypes;

export { LeadPage };
export default connect(mapStateToProps)(LeadPage);
